import { Box, Typography } from "@mui/material";
import { InlineInput } from "components/FormControls/InlineInput/InlineInput";
import { OLMenuButton } from "components/FormControls/OLMenuButton";
import { ReactComponent as ChevronUpIcon } from "components/Icons/chevron-up.svg";
import { Tooltip } from "components/Tooltips/Tooltip";
import { FieldArray } from "formik";
import { useMessage } from "hooks/intl";
import { useIsOverflown } from "hooks/useIsOverflown";
import { useState } from "react";
import { TransactionItemFilterType } from "types/campaigns";
import { useStyles } from "../CampaignTransactionItemFilters.styles";
import { itemFilters } from "../helpers";
import { TransactionItemFilterCondition } from "./TransactionItemFilterCondition/TransactionItemFilterCondition";

interface Props {
  transactionItemFilterIndex: number;
  name: string;
  setFieldValue: (field: string, value: string) => void;
  error?: any;
}

const maxFiltersCount = 6;

export const TransactionItemFilter = ({
  transactionItemFilterIndex,
  name,
  setFieldValue,
  error,
}: Props) => {
  const [isFilterContainerOpen, setIsFilterContainerOpen] = useState(true);

  const classes = useStyles({ isFilterContainerOpen });
  const { message } = useMessage();
  const { observeElement, isOverflown } = useIsOverflown();

  const formatCode = (value: string) =>
    value.trim().replace(/ /g, "_").toLowerCase();

  const code = name.length > 0 ? formatCode(name) : "";

  return (
    <Box
      className={classes.filterContainer}
      data-test-id={`transaction-item-filter-${transactionItemFilterIndex}`}
    >
      <div className={classes.topFunctions}>
        <Tooltip
          disableHoverListener={!isOverflown}
          text={
            name ? (
              <Typography className={classes.tooltipTitle}>{name}</Typography>
            ) : (
              ""
            )
          }
        >
          <div className={classes.filterHeader} ref={observeElement}>
            <InlineInput
              name={`transactionItemsFilters[${transactionItemFilterIndex}].name`}
              options={{
                disablePreviewTooltip: true,
              }}
              className={classes.filterName}
              autoFocus
              onEditModeClose={(value) => {
                setFieldValue(
                  `transactionItemsFilters[${transactionItemFilterIndex}].code`,
                  formatCode(value as string),
                );
              }}
            />
          </div>
        </Tooltip>
        <div className={classes.codeAndArrowWrapper}>
          <Tooltip
            text={
              <Typography className={classes.tooltipTitle}>{code}</Typography>
            }
            disableHoverListener={!isOverflown}
            placement="bottom-start"
          >
            <span className={classes.code} ref={observeElement}>
              {code}
            </span>
          </Tooltip>
          <ChevronUpIcon
            onClick={() => setIsFilterContainerOpen(!isFilterContainerOpen)}
            className={classes.arrow}
            data-test-id={`filter-${transactionItemFilterIndex}-arrow`}
          />
        </div>
      </div>
      {isFilterContainerOpen ? (
        <FieldArray
          name={`transactionItemsFilters[${transactionItemFilterIndex}].filters`}
          render={({
            push,
            remove,
            replace,
            insert,
            form: { values, setFieldValue },
          }) => {
            const filters: TransactionItemFilterType[] =
              values.transactionItemsFilters[transactionItemFilterIndex]
                .filters;

            return (
              <>
                {filters.map((filter, filterIndex) => {
                  const conditionError =
                    error?.filters && error.filters[filterIndex]?.condition;

                  return (
                    <TransactionItemFilterCondition
                      key={filterIndex}
                      isFilterContainerOpen={isFilterContainerOpen}
                      transactionItemFilterIndex={transactionItemFilterIndex}
                      filterIndex={filterIndex}
                      replace={replace}
                      filter={filter}
                      setFieldValue={setFieldValue}
                      remove={remove}
                      insert={insert}
                      transactionItemsFilters={values.transactionItemsFilters}
                      error={conditionError}
                    />
                  );
                })}
                <Box className={classes.buttonWrapper}>
                  <OLMenuButton
                    className={
                      filters.length >= maxFiltersCount
                        ? classes.newConditionButtonDisabled
                        : ""
                    }
                    buttonVariant="outlined"
                    data-test-id="add-new-condition"
                    items={itemFilters.map((item) => {
                      return {
                        label: message({ id: item.label }),
                        onClick: () => {
                          push({
                            value: {
                              field: item.id,
                            },
                            type: item.type,
                            label: item.label,
                            isSaved: false,
                            isEditMode: false,
                          });
                        },
                      };
                    })}
                    hideSearch
                    submit={message({
                      id: "addNewCondition",
                    })}
                    disabled={filters.length >= maxFiltersCount}
                  />
                </Box>
              </>
            );
          }}
        />
      ) : null}
    </Box>
  );
};
